import React, { useState, useEffect, useRef, useReducer } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { parsePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js';
import { debounce } from 'lodash';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, BarChart, Bar
} from 'recharts';
import {
    Calendar, CreditCard, DollarSign, TrendingUp, Bell, Plus, User, Home, RefreshCw, AlertCircle, Phone, MessageSquare, CheckCircle
} from 'lucide-react';
import { db } from '../firebase';
import { doc, getDoc, deleteDoc } from 'firebase/firestore';
import moment from 'moment-timezone';

// Sample data for charts (replace with real API data)
const sampleExpenseData = [
    { date: '2025-09-01', amount: 500 },
    { date: '2025-09-02', amount: 750 },
    { date: '2025-09-03', amount: 300 },
    { date: '2025-09-04', amount: 900 },
    { date: '2025-09-05', amount: 600 },
];

const sampleCategoryData = [
    { name: 'Comida', value: 1200 },
    { name: 'Transporte', value: 800 },
    { name: 'Entretenimiento', value: 500 },
    { name: 'Salud', value: 300 },
    { name: 'Otros', value: 200 },
];

const COLORS = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FECA57'];

// Auth state reducer
const authReducer = (state, action) => {
    switch (action.type) {
        case 'SET_PHONE':
            return { ...state, phoneNumber: action.payload, error: '' };
        case 'SET_CODE':
            return { ...state, verificationCode: action.payload, error: '' };
        case 'SET_STEP':
            return { ...state, authStep: action.payload };
        case 'SET_LOADING':
            return { ...state, loading: action.payload };
        case 'SET_ERROR':
            return { ...state, error: action.payload };
        case 'SET_AUTHENTICATED':
            return { ...state, isAuthenticated: action.payload, userId: action.userId || state.userId };
        case 'SET_RETRY_COUNT':
            return { ...state, retryCount: action.payload };
        case 'RESET':
            return {
                isAuthenticated: false,
                authStep: 'phone',
                phoneNumber: '',
                verificationCode: '',
                loading: false,
                error: '',
                userId: '',
                retryCount: 0
            };
        default:
            return state;
    }
};

const FinancialDashboard = () => {
    // Auth state management
    const [authState, dispatch] = useReducer(authReducer, {
        isAuthenticated: false,
        authStep: 'phone',
        phoneNumber: '',
        verificationCode: '',
        loading: false,
        error: '',
        userId: '',
        retryCount: 0
    });

    // Dashboard state
    const [activeTab, setActiveTab] = useState('dashboard');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [expenses, setExpenses] = useState([]);
    const [subscriptions, setSubscriptions] = useState([]);
    const [reminders, setReminders] = useState([]);
    const [analytics, setAnalytics] = useState(null);
    const [systemStats, setSystemStats] = useState(null);

    // Form refs
    const phoneInputRef = useRef(null);
    const codeInputRef = useRef(null);

    // API base URL
    const API_BASE = 'https://us-central1-xfinance-12e10.cloudfunctions.net';

    // WhatsApp business number
    const WHATSAPP_BUSINESS_NUMBER = '+5215661642824';

    // Check for saved session
    useEffect(() => {
        const savedAuth = localStorage.getItem('financeapp_auth');
        if (savedAuth) {
            const authData = JSON.parse(savedAuth);
            dispatch({ type: 'SET_AUTHENTICATED', payload: true, userId: authData.userId });
            dispatch({ type: 'SET_PHONE', payload: authData.phoneNumber });
            dispatch({ type: 'SET_STEP', payload: 'authenticated' });
        }
    }, []);

    // Load data when authenticated and focus inputs
    useEffect(() => {
        if (authState.isAuthenticated && authState.userId) {
            loadAllData();
        }
        if (authState.authStep === 'phone' && phoneInputRef.current) {
            phoneInputRef.current.focus();
        } else if (authState.authStep === 'verification' && codeInputRef.current) {
            codeInputRef.current.focus();
        }
    }, [authState.isAuthenticated, authState.userId, authState.authStep]);

    // Format and validate phone number to match backend format (521 + 10-digit number)
    const formatPhoneNumber = (phone) => {
        try {
            const phoneNumber = parsePhoneNumber(phone, 'MX');
            if (phoneNumber && isValidPhoneNumber(phone, 'MX')) {
                // Backend expects 521 + 10-digit number (e.g., 5216621215131 for +526621215131)
                const nationalNumber = phoneNumber.nationalNumber;
                const formatted = `52${nationalNumber.length === 10 ? '1' : ''}${nationalNumber}`;
                console.log('Formatted phone number:', formatted);
                return formatted;
            }
            console.error('Invalid phone number:', phone);
            return phone;
        } catch (err) {
            console.error('Error formatting phone number:', err.message);
            return phone;
        }
    };

    // Verify code using Firestore
    const verifyCode = async () => {
        if (authState.retryCount >= 3) {
            dispatch({
                type: 'SET_ERROR',
                payload: `Demasiados intentos. Envía un nuevo mensaje con "Código" al ${WHATSAPP_BUSINESS_NUMBER} e intenta de nuevo.`
            });
            return;
        }

        dispatch({ type: 'SET_LOADING', payload: true });
        dispatch({ type: 'SET_ERROR', payload: '' });

        try {
            const formattedPhone = formatPhoneNumber(authState.phoneNumber);
            console.log('Verifying code for userId:', formattedPhone, 'with code:', authState.verificationCode);

            if (!isValidPhoneNumber(authState.phoneNumber, 'MX')) {
                throw new Error('Número de teléfono inválido');
            }

            // Query Firestore for the verification code
            const codeRef = doc(db, 'verification_codes', formattedPhone);
            const codeDoc = await getDoc(codeRef);

            if (!codeDoc.exists()) {
                console.error('No code document found for userId:', formattedPhone);
                throw new Error(`Código no encontrado o expirado. Envía un nuevo mensaje con "Código" al ${WHATSAPP_BUSINESS_NUMBER}`);
            }

            const data = codeDoc.data();
            console.log('Firestore document data:', data);

            const now = moment().tz('America/Phoenix');
            const expiresAt = moment(data.expiresAt.toDate());

            if (now.isAfter(expiresAt)) {
                console.log('Code expired:', { now: now.toISOString(), expiresAt: expiresAt.toISOString() });
                await deleteDoc(codeRef);
                throw new Error(`Código expirado. Envía un nuevo mensaje con "Código" al ${WHATSAPP_BUSINESS_NUMBER}`);
            }

            if (data.code !== authState.verificationCode) {
                console.log('Invalid code:', { expected: data.code, provided: authState.verificationCode });
                throw new Error('Código incorrecto');
            }

            // Code is valid, delete it to prevent reuse
            await deleteDoc(codeRef);
            console.log('Verification successful, code deleted for userId:', formattedPhone);

            const authData = {
                userId: formattedPhone,
                phoneNumber: authState.phoneNumber,
                authenticated: true,
                timestamp: new Date().toISOString()
            };

            localStorage.setItem('financeapp_auth', JSON.stringify(authData));

            dispatch({ type: 'SET_AUTHENTICATED', payload: true, userId: formattedPhone });
            dispatch({ type: 'SET_STEP', payload: 'authenticated' });
            dispatch({ type: 'SET_RETRY_COUNT', payload: 0 });

        } catch (err) {
            console.error('Verification error:', err.message);
            dispatch({ type: 'SET_RETRY_COUNT', payload: authState.retryCount + 1 });
            dispatch({ type: 'SET_ERROR', payload: err.message });
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    };

    // Logout
    const logout = () => {
        localStorage.removeItem('financeapp_auth');
        dispatch({ type: 'RESET' });
        setExpenses([]);
        setSubscriptions([]);
        setReminders([]);
        setAnalytics(null);
        setSystemStats(null);
    };

    // Debounced data loading
    const loadAllData = debounce(async () => {
        setLoading(true);
        setError(null);

        try {
            await Promise.all([
                loadSystemStats(),
                loadReminders(),
                loadExpenses(),
                loadSubscriptions(),
                loadAnalytics()
            ]);
        } catch (err) {
            setError('Error cargando datos: ' + err.message);
        } finally {
            setLoading(false);
        }
    }, 500);

    // Load system stats
    const loadSystemStats = async () => {
        try {
            const response = await fetch(`${API_BASE}/getSystemStats`);
            if (!response.ok) throw new Error('Error en la respuesta');
            const data = await response.json();
            setSystemStats(data.stats);
        } catch (err) {
            console.error('Error loading system stats:', err);
        }
    };

    // Load reminders
    const loadReminders = async () => {
        try {
            const response = await fetch(`${API_BASE}/getUserReminders?userId=${authState.userId}`);
            if (!response.ok) throw new Error('Error cargando recordatorios');
            const data = await response.json();
            if (data.success) {
                setReminders(data.reminders || []);
            }
        } catch (err) {
            console.error('Error loading reminders:', err);
        }
    };

    // Load expenses
    const loadExpenses = async () => {
        try {
            const response = await fetch(`${API_BASE}/getUserExpenses?userId=${authState.userId}`);
            if (!response.ok) throw new Error('Error cargando gastos');
            const data = await response.json();
            if (data.success) {
                setExpenses(data.expenses || []);
            }
        } catch (err) {
            console.error('Error loading expenses:', err);
        }
    };

    // Load subscriptions
    const loadSubscriptions = async () => {
        try {
            const response = await fetch(`${API_BASE}/getUserSubscriptions?userId=${authState.userId}`);
            if (!response.ok) throw new Error('Error cargando suscripciones');
            const data = await response.json();
            if (data.success) {
                setSubscriptions(data.subscriptions || []);
            }
        } catch (err) {
            console.error('Error loading subscriptions:', err);
        }
    };

    // Load analytics
    const loadAnalytics = async () => {
        try {
            const response = await fetch(`${API_BASE}/getFinancialOverview?userId=${authState.userId}`);
            if (!response.ok) throw new Error('Error cargando analíticas');
            const data = await response.json();
            if (data.success) {
                setAnalytics(data.overview);
            }
        } catch (err) {
            console.error('Error loading analytics:', err);
        }
    };

    // Generic API call
    const callAPI = async (endpoint, method = 'GET', body = null) => {
        try {
            const options = {
                method,
                headers: { 'Content-Type': 'application/json' },
            };
            if (body) options.body = JSON.stringify(body);
            const response = await fetch(`${API_BASE}${endpoint}`, options);
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
            }
            return await response.json();
        } catch (error) {
            console.error(`Error calling ${endpoint}:`, error);
            throw error;
        }
    };

    // Create new reminder
    const createReminder = async (message, date) => {
        try {
            setLoading(true);
            await callAPI('/createReminderAPI', 'POST', {
                userId: authState.userId,
                message,
                date
            });
            await loadReminders();
        } catch (err) {
            setError('Error creando recordatorio: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    // Create new expense
    const createExpense = async (amount, category, date) => {
        try {
            setLoading(true);
            await callAPI('/createExpense', 'POST', {
                userId: authState.userId,
                amount,
                categoryId: category.id,
                categoryName: category.name,
                categoryIcon: category.icon,
                date
            });
            await loadExpenses();
        } catch (err) {
            setError('Error creando gasto: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    // Phone Auth Step
    const PhoneAuthStep = () => (
        <div className="min-h-screen bg-gradient-to-b from-blue-50 to-gray-100 flex flex-col items-center justify-center p-4 font-sans">
            <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md transform transition-all duration-300">
                <div className="text-center mb-6">
                    <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Phone className="h-8 w-8 text-blue-600" />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">FinanceApp</h1>
                    <p className="text-gray-600 text-sm">Ingresa tu número de WhatsApp para recibir un código</p>
                </div>

                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Número de WhatsApp
                        </label>
                        <PhoneInput
                            inputProps={{
                                ref: phoneInputRef,
                                autoFocus: true,
                                name: 'phone',
                                className: 'w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 transition-all duration-200'
                            }}
                            country={'mx'}
                            value={authState.phoneNumber}
                            onChange={(phone) => dispatch({ type: 'SET_PHONE', payload: phone })}
                            enableSearch
                            placeholder="Ingresa tu número"
                            countryCodeEditable={false}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Selecciona tu país y escribe tu número completo
                        </p>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                        <div className="flex items-start">
                            <MessageSquare className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                            <div>
                                <p className="text-blue-800 text-sm font-medium">Solicita tu código</p>
                                <p className="text-blue-700 text-xs mt-1">
                                    Envía <strong>"Código"</strong> al WhatsApp {WHATSAPP_BUSINESS_NUMBER} para recibir tu código.
                                </p>
                            </div>
                        </div>
                    </div>

                    <a
                        href={`https://wa.me/${WHATSAPP_BUSINESS_NUMBER}?text=C%C3%B3digo`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full bg-green-600 text-white py-3 px-4 rounded-xl hover:bg-green-700 transition-all duration-200 flex items-center justify-center shadow-md"
                    >
                        <MessageSquare className="h-5 w-5 mr-2" />
                        Enviar "Código" por WhatsApp
                    </a>

                    <button
                        onClick={() => dispatch({ type: 'SET_STEP', payload: 'verification' })}
                        disabled={!isValidPhoneNumber(authState.phoneNumber, 'MX')}
                        className="w-full bg-blue-600 text-white py-3 px-4 rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center shadow-md"
                    >
                        <CheckCircle className="h-5 w-5 mr-2" />
                        Ingresar código recibido
                    </button>

                    {authState.error && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                            <p className="text-red-800 text-sm">{authState.error}</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    // Verification Step
    const VerificationStep = () => (
        <div className="min-h-screen bg-gradient-to-b from-blue-50 to-gray-100 flex flex-col items-center justify-center p-4 font-sans">
            <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md transform transition-all duration-300">
                <div className="text-center mb-6">
                    <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                        <MessageSquare className="h-8 w-8 text-green-600" />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">Verificar Código</h1>
                    <p className="text-gray-600 text-sm">Ingresa el código de 6 dígitos recibido en WhatsApp</p>
                    <p className="text-sm text-gray-500 mt-1">{authState.phoneNumber}</p>
                </div>

                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Código de verificación
                        </label>
                        <input
                            ref={codeInputRef}
                            type="text"
                            value={authState.verificationCode}
                            onChange={(e) => dispatch({ type: 'SET_CODE', payload: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                            placeholder="123456"
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-xl tracking-widest transition-all duration-200"
                            maxLength={6}
                            autoFocus
                        />
                    </div>

                    {authState.error && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                            <p className="text-red-800 text-sm">{authState.error}</p>
                            <p className="text-red-800 text-sm mt-2">
                                ¿No recibiste el código? Envía "Código" al {WHATSAPP_BUSINESS_NUMBER}.
                            </p>
                        </div>
                    )}

                    <button
                        onClick={verifyCode}
                        disabled={authState.loading || authState.verificationCode.length !== 6}
                        className="w-full bg-green-600 text-white py-3 px-4 rounded-xl hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center shadow-md"
                    >
                        {authState.loading ? (
                            <RefreshCw className="h-5 w-5 animate-spin mr-2" />
                        ) : (
                            <CheckCircle className="h-5 w-5 mr-2" />
                        )}
                        Verificar Código
                    </button>

                    <div className="flex space-x-4">
                        <button
                            onClick={() => {
                                dispatch({ type: 'SET_STEP', payload: 'phone' });
                                dispatch({ type: 'SET_CODE', payload: '' });
                                dispatch({ type: 'SET_ERROR', payload: '' });
                                dispatch({ type: 'SET_RETRY_COUNT', payload: 0 });
                            }}
                            className="flex-1 text-gray-600 py-2 px-4 rounded-xl hover:bg-gray-100 transition-all duration-200"
                        >
                            Cambiar número
                        </button>
                        <a
                            href={`https://wa.me/${WHATSAPP_BUSINESS_NUMBER}?text=C%C3%B3digo`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 text-blue-600 py-2 px-4 rounded-xl hover:bg-blue-50 transition-all duration-200 text-center"
                        >
                            Reenviar "Código"
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );

    // StatCard Component
    const StatCard = ({ title, value, icon: Icon, change, color = "blue", loading = false }) => (
        <div className="bg-white rounded-xl shadow-md p-4 transform transition-all duration-200 hover:scale-105">
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-sm font-medium text-gray-600">{title}</div>
                    <div className="text-xl font-bold text-gray-900 mt-2">
                        {loading ? (
                            <div className="animate-pulse bg-gray-200 h-8 w-20 rounded"></div>
                        ) : (
                            value
                        )}
                    </div>
                    {change !== undefined && (
                        <div className={`text-sm mt-2 ${change > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {change > 0 ? '+' : ''}{change}% vs mes anterior
                        </div>
                    )}
                </div>
                <div className={`p-3 rounded-full bg-${color}-100`}>
                    <Icon className={`h-6 w-6 text-${color}-600`} />
                </div>
            </div>
        </div>
    );

    // Navigation Component (Bottom Navigation for Mobile)
    const Navigation = () => (
        <nav className="fixed bottom-0 left-0 right-0 bg-white shadow-t-lg border-t border-gray-200 z-10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
                <div className="flex justify-around py-2">
                    {[
                        { id: 'dashboard', name: 'Inicio', icon: Home },
                        { id: 'expenses', name: 'Gastos', icon: DollarSign },
                        { id: 'subscriptions', name: 'Suscripciones', icon: CreditCard },
                        { id: 'reminders', name: 'Recordatorios', icon: Bell },
                        { id: 'analytics', name: 'Analíticas', icon: TrendingUp },
                    ].map(({ id, name, icon: Icon }) => (
                        <button
                            key={id}
                            onClick={() => setActiveTab(id)}
                            className={`flex flex-col items-center px-2 py-2 text-sm font-medium ${activeTab === id ? 'text-blue-600' : 'text-gray-500 hover:text-blue-600'
                                } transition-all duration-200`}
                            aria-current={activeTab === id ? 'page' : undefined}
                        >
                            <Icon className="h-6 w-6 mb-1" />
                            <span>{name}</span>
                        </button>
                    ))}
                </div>
            </div>
        </nav>
    );

    // Dashboard View
    const DashboardView = () => (
        <div className="space-y-6 pb-20">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    title="Total Usuarios"
                    value={systemStats?.totalUsers || 0}
                    icon={User}
                    color="blue"
                    loading={loading}
                />
                <StatCard
                    title="Recordatorios"
                    value={systemStats?.totalReminders || 0}
                    icon={Bell}
                    color="green"
                    loading={loading}
                />
                <StatCard
                    title="Gastos Registrados"
                    value={systemStats?.totalExpenses || 0}
                    icon={DollarSign}
                    color="yellow"
                    loading={loading}
                />
                <StatCard
                    title="Suscripciones Activas"
                    value={systemStats?.totalActiveSubscriptions || 0}
                    icon={CreditCard}
                    color="purple"
                    loading={loading}
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-white rounded-xl shadow-md p-4 transform transition-all duration-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Recordatorios Pendientes</h3>
                    {reminders.length === 0 ? (
                        <p className="text-gray-500 text-sm">No hay recordatorios pendientes</p>
                    ) : (
                        <div className="space-y-3">
                            {reminders.slice(0, 5).map((reminder, index) => (
                                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <div>
                                        <p className="font-medium text-gray-900 text-sm">{reminder.message}</p>
                                        <p className="text-xs text-gray-500">{reminder.date}</p>
                                    </div>
                                    <Bell className="h-4 w-4 text-blue-500" />
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="bg-white rounded-xl shadow-md p-4 transform transition-all duration-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Estadísticas del Sistema</h3>
                    {systemStats && (
                        <div className="space-y-3">
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Interacciones Hoy:</span>
                                <span className="font-semibold">{systemStats.todayInteractions}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Interacciones Ayer:</span>
                                <span className="font-semibold">{systemStats.yesterdayInteractions}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Crecimiento:</span>
                                <span className={`font-semibold ${systemStats.growthRate > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {systemStats.growthRate}%
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    // Expenses View
    const ExpensesView = () => (
        <div className="space-y-6 pb-20">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900">Gastos</h2>
                <button
                    onClick={() => {
                        const amount = prompt('Monto del gasto (MXN):');
                        const category = prompt('Categoría (ej: Comida):');
                        const date = prompt('Fecha (YYYY-MM-DD):');
                        if (amount && category && date) {
                            createExpense(Number(amount), { name: category, id: category, icon: '📝' }, date);
                        }
                    }}
                    className="bg-blue-600 text-white px-4 py-2 rounded-xl hover:bg-blue-700 flex items-center text-sm transition-all duration-200 shadow-md"
                >
                    <Plus className="h-4 w-4 mr-2" />
                    Nuevo Gasto
                </button>
            </div>

            <div className="bg-white rounded-xl shadow-md p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Resumen de Gastos</h3>
                {expenses.length === 0 ? (
                    <div className="text-center py-8">
                        <DollarSign className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500 text-sm">No hay gastos registrados</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={sampleExpenseData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" />
                                <YAxis />
                                <Tooltip />
                                <Bar dataKey="amount" fill="#4ECDC4" />
                            </BarChart>
                        </ResponsiveContainer>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {expenses.map((expense, index) => (
                                <div key={index} className="border border-gray-200 rounded-lg p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h3 className="font-semibold text-gray-900 text-sm">{expense.categoryName}</h3>
                                            <p className="text-xs text-gray-500 mt-1">{expense.date}</p>
                                            <p className="text-sm font-medium text-gray-900 mt-1">${expense.amount} MXN</p>
                                        </div>
                                        <span className="text-gray-500">{expense.categoryIcon}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    // Subscriptions View
    const SubscriptionsView = () => (
        <div className="space-y-6 pb-20">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900">Suscripciones</h2>
                <button
                    onClick={() => {
                        alert('Funcionalidad de creación de suscripciones en desarrollo');
                    }}
                    className="bg-blue-600 text-white px-4 py-2 rounded-xl hover:bg-blue-700 flex items-center text-sm transition-all duration-200 shadow-md"
                >
                    <Plus className="h-4 w-4 mr-2" />
                    Nueva Suscripción
                </button>
            </div>

            <div className="bg-white rounded-xl shadow-md p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Suscripciones Activas</h3>
                {subscriptions.length === 0 ? (
                    <div className="text-center py-8">
                        <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500 text-sm">No hay suscripciones activas</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {subscriptions.map((subscription, index) => (
                            <div key={index} className="border border-gray-200 rounded-lg p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="font-semibold text-gray-900 text-sm">{subscription.name}</h3>
                                        <p className="text-xs text-gray-500 mt-1">Próximo pago: {subscription.nextBillingDate}</p>
                                        <p className="text-sm font-medium text-gray-900 mt-1">${subscription.amount} MXN</p>
                                    </div>
                                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                                        {subscription.billingCycle}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );

    // Analytics View
    const AnalyticsView = () => (
        <div className="space-y-6 pb-20">
            <h2 className="text-xl font-bold text-gray-900">Analíticas Financieras</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-white rounded-xl shadow-md p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Gastos por Categoría</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie
                                data={sampleCategoryData}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={100}
                                label
                            >
                                {sampleCategoryData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
                <div className="bg-white rounded-xl shadow-md p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Tendencia de Gastos</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={sampleExpenseData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" />
                            <YAxis />
                            <Tooltip />
                            <Line type="monotone" dataKey="amount" stroke="#FF6B6B" />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>
            {analytics && (
                <div className="bg-white rounded-xl shadow-md p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Resumen Financiero</h3>
                    <div className="space-y-3">
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Obligaciones Mensuales:</span>
                            <span className="font-semibold">${analytics.totalMonthlyObligations} MXN</span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Salud Crediticia:</span>
                            <span className="font-semibold">{analytics.healthIndicators.creditHealth}%</span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Control de Gastos:</span>
                            <span className="font-semibold">{analytics.healthIndicators.expenseControl}%</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    // Reminders View
    const RemindersView = () => (
        <div className="space-y-6 pb-20">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900">Recordatorios</h2>
                <button
                    onClick={() => {
                        const message = prompt('Mensaje del recordatorio:');
                        const date = prompt('Fecha (YYYY-MM-DD HH:MM):');
                        if (message && date) {
                            createReminder(message, date);
                        }
                    }}
                    className="bg-blue-600 text-white px-4 py-2 rounded-xl hover:bg-blue-700 flex items-center text-sm transition-all duration-200 shadow-md"
                >
                    <Plus className="h-4 w-4 mr-2" />
                    Nuevo Recordatorio
                </button>
            </div>

            <div className="bg-white rounded-xl shadow-md p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Recordatorios Pendientes</h3>
                {reminders.length === 0 ? (
                    <div className="text-center py-8">
                        <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500 text-sm">No tienes recordatorios pendientes</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {reminders.map((reminder, index) => (
                            <div key={index} className="border border-gray-200 rounded-lg p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="font-semibold text-gray-900 text-sm">{reminder.message}</h3>
                                        <p className="text-xs text-gray-500 mt-1">{reminder.date}</p>
                                    </div>
                                    <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                                        Pendiente
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );

    // Render content based on active tab
    const renderContent = () => {
        switch (activeTab) {
            case 'dashboard': return <DashboardView />;
            case 'expenses': return <ExpensesView />;
            case 'subscriptions': return <SubscriptionsView />;
            case 'reminders': return <RemindersView />;
            case 'analytics': return <AnalyticsView />;
            default: return <DashboardView />;
        }
    };

    // Render based on authentication state
    if (!authState.isAuthenticated) {
        if (authState.authStep === 'phone') return <PhoneAuthStep />;
        if (authState.authStep === 'verification') return <VerificationStep />;
    }

    return (
        <div className="min-h-screen bg-gray-50 pt-4 pb-16">
            <main className="max-w-7xl mx-auto px-4">
                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
                        <div className="flex items-center">
                            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                            <p className="text-red-800 text-sm">{error}</p>
                            <button
                                onClick={() => setError(null)}
                                className="ml-auto text-red-500 hover:text-red-700"
                                aria-label="Cerrar error"
                            >
                                ×
                            </button>
                        </div>
                    </div>
                )}
                {renderContent()}
            </main>
            <Navigation />
        </div>
    );
};

export default FinancialDashboard;